import { AttachmentList } from '@common/data-models/complex-types/attachment-list';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { Table } from '@common/data-models/complex-types/table';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { Record } from '@common/data-models/records/record';
import { ObjectTypes } from '@common/enums/object-types';
import { IPlaybookService } from '@playbook/interfaces/i-playbook-service';
import { Address } from '@suite/common/data-models/complex-types/address';

export type AssignmentCoverageType = 'Text' | 'Photo' | 'Live Video' | 'Video' | 'Sound' | 'Graphic' | 'Interactive' | 'Social' | 'SocialPromotion' | 'LiveVideo';

export class AssignmentRecord extends Record {
    public static playbookService: IPlaybookService;

    public initialize() {
        this.type = ObjectTypes.ASSIGNMENT;
        super.initialize();

        this.department = new MultiValue<string>();
        this.attachments = new AttachmentList();
        this.storyOwner = new MultiValue<string>();
        this.newsEditor = new MultiValue<string>();
        this.status = '';

        this.setupRequiredFieldsGroups('Assignment');
    }

    protected getExcludedFields(): { [id: string]: boolean } {
        const excluded: { [id: string]: boolean } = {};
        excluded[PSTFieldNames.Title] = true;
        excluded[PSTFieldNames.PSTDesk] = true;
        excluded[PSTFieldNames.DescriptionType] = true;
        excluded[PSTFieldNames.PSTAssignmentCost] = true;
        excluded[PSTFieldNames.PSTTags] = true;
        excluded[PSTFieldNames.PSTAssignmentStatus] = true;
        excluded[PSTFieldNames.Description] = true;
        excluded[PSTFieldNames.PSTExternalFiles] = true;
        excluded[PSTFieldNames.PSTApproved] = true;
        return excluded;
    }

    public topicMetadata: { guid: string; slug: string; category: string; location: string; owner: string; type: MultiValue<string> };

    public get parentGuid(): string {
        let parentGuid = this.getAs<string>('ParentGuid');
        if (!parentGuid) {
            // Extract parent guid from the path
            if (this.path) {
                parentGuid = this.path.parentCollectionGuid;
            }
        }
        return parentGuid;
    }
    public set parentGuid(value: string) {
        this.set('ParentGuid', value);
    }

    public get storyOwner(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTAssignmentOwner);
    }
    public set storyOwner(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTAssignmentOwner, value);
    }

    public get cost(): Table {
        return this.getAs<Table>(PSTFieldNames.PSTAssignmentCost);
    }
    public set cost(value: Table) {
        this.set(PSTFieldNames.PSTAssignmentCost, value);
    }

    public get coverage(): string {
        return this.getAs<string>(PSTFieldNames.Description);
    }
    public set coverage(value: string) {
        this.set(PSTFieldNames.Description, value);
    }

    public get newsEditor(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTAssignmentNewsEditor);
    }
    public set newsEditor(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTAssignmentNewsEditor, value);
    }

    public get tags(): LookupList {
        return this.getAs<LookupList>(PSTFieldNames.PSTTags, new LookupList());
    }
    public set tags(value: LookupList) {
        this.set(PSTFieldNames.PSTTags, value);
    }

    public get coverageType(): AssignmentCoverageType {
        return this.getAs<AssignmentCoverageType>(PSTFieldNames.DescriptionType);
    }
    public set coverageType(value: AssignmentCoverageType) {
        this.set(PSTFieldNames.DescriptionType, value);
    }

    public get department(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTDesk);
    }
    public set department(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTDesk, value);
    }

    public get assignmentSlug(): string {
        return this.getAs<string>(PSTFieldNames.Title);
    }
    public set assignmentSlug(value: string) {
        this.set(PSTFieldNames.Title, value);
    }

    public get status(): string {
        return this.getAs<string>(PSTFieldNames.PSTAssignmentStatus);
    }
    public set status(value: string) {
        this.set(PSTFieldNames.PSTAssignmentStatus, value);
    }

    public get fileDate(): DateTime {
        return this.getAs<DateTime>(PSTFieldNames.TargetPublicationDate);
    }
    public set fileDate(value: DateTime) {
        this.set(PSTFieldNames.TargetPublicationDate, value);
    }

    get attachments(): AttachmentList {
        return this.getAs<AttachmentList>(PSTFieldNames.PSTExternalFiles);
    }
    set attachments(value: AttachmentList) {
        this.set(PSTFieldNames.PSTExternalFiles, value);
    }

    public get pstApproved(): boolean {
        return this.getAs<boolean>(PSTFieldNames.PSTApproved);
    }
    public set pstApproved(value: boolean) {
        this.set(PSTFieldNames.PSTApproved, value);
    }

    public get parentCategory(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentCategory, new MultiValue<string>());
    }
    public set parentCategory(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentCategory, value);
    }

    public get parentOwner(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentOwner, new MultiValue<string>());
    }
    public set parentOwner(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentOwner, value);
    }

    public get parentLocation(): Address {
        return this.getAs<Address>(PSTFieldNames.PSTParentLocation, new Address());
    }
    public set parentLocation(value: Address) {
        this.set(PSTFieldNames.PSTParentLocation, value);
    }

    public get parentType(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTParentType);
    }
    public set parentType(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTParentType, value);
    }

    public get getSecurityPrivsWrite(): boolean {
        const desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTDesk);
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
    }

    public getSecurityPrivsRead(): boolean {
        const desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTDesk);
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
    }

    private static _fieldDefsDict: { [id: string]: FieldDef } = null;
    public get fieldDefsDict(): { [id: string]: FieldDef } {
        if (!AssignmentRecord._fieldDefsDict) {
            AssignmentRecord._fieldDefsDict = {};
            for (const key in Record.globalTablesService.FieldDefsDict) {
                if (Record.globalTablesService.FieldDefsDict[key].AssignmentProp) {
                    AssignmentRecord._fieldDefsDict[key] = Record.globalTablesService.FieldDefsDict[key];
                }
            }
        }
        return AssignmentRecord._fieldDefsDict;
    }

    get getRequiredProperties(): { [id: string]: FieldDef } {
        return AssignmentRecord.playbookService.AssignmentRequiredFieldDefsDict;
    }

    public parentChanged() {
        if (this.parentGuid && this.parentRecord) {
            if (!this.topicMetadata) {
                this.topicMetadata = {
                    guid: null,
                    slug: null,
                    category: null,
                    location: null,
                    owner: null,
                    type: null,
                };
            }
            this.topicMetadata.guid = this.parentRecord.guid;
            this.topicMetadata.slug = this.parentRecord.title;
            const owners = this.parentRecord.storyOwner;
            this.topicMetadata.owner = owners.map(prop => Record.globalTablesService.Staff[prop]?.DisplayName || prop).join(', ');

            this.topicMetadata.category = (this.parentRecord as unknown as StorytellingTopicRecord).PSTCategory.toString();
            this.topicMetadata.type = (this.parentRecord as unknown as StorytellingTopicRecord).PSTTopicType;
            this.topicMetadata.location = (this.parentRecord as unknown as StorytellingTopicRecord).PSTTopicAddress.title;
        }
    }
}
